/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Button, Heading, HFlow, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Form, FormDebouncedValueSpy, SubmitButton } from 'components/form'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { getFieldError } from 'components/form/final-form/util'
import { confirm } from 'components/modals/confirm'
import { FormApi } from 'final-form'
import { IvcfPerguntaEnum, IvcfSubgrupoPerguntaEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { isEmpty } from 'lodash'
import { Fragment, MutableRefObject, useMemo } from 'react'
import { useField } from 'react-final-form'
import { calculateAge } from 'util/date/calculateAge'
import { humanizeAge } from 'util/date/humanize-age'
import { MetaPath, metaPath } from 'util/metaPath'
import { meta as soapMeta } from 'view/atendimentos/atendimento-individual/model'

import { FormAtivoObjetivoEnum } from '../../../components/SwitchButtonObjetivoForm'
import { IvcfAplicacaoModel, IvcfFieldModel, IvcfGrupoEnum } from '../../model-ivcf'
import { calculateIvcf } from '../../util-ivcf'
import { IvcfGrupoHeader } from '../components/IvcfGrupoHeader'
import { ivcfDecorator } from './calculator-ivcf'
import { IvcfPerguntaField } from './IvcfPerguntaField'
import { IvcfSubgrupoField } from './IvcfSubgrupoField'
import { ivcfValidator } from './validator-ivcf'

interface IvcfAplicacaoFormProps {
  name: MetaPath<IvcfFieldModel>
  cidadaoDataNascimento: string
  dataInicioAtendimento: string
  rootRef: MutableRefObject<HTMLDivElement>
  initialValues?: IvcfAplicacaoModel
  hideHeader?: boolean
  hideBorder?: boolean
  readOnly?: boolean
  isEditing?: boolean
  onSubmit(values: IvcfFieldModel): void
  updateCache?(values: IvcfAplicacaoModel): void
  onCancelEdit?(): void
  setSubmitFailed?(value: boolean): void
}

const formPath = metaPath<IvcfAplicacaoModel>()

export function IvcfAplicacaoForm(props: IvcfAplicacaoFormProps) {
  const {
    name,
    cidadaoDataNascimento,
    dataInicioAtendimento,
    rootRef,
    initialValues,
    hideHeader,
    hideBorder,
    readOnly,
    isEditing,
    onSubmit,
    updateCache,
    onCancelEdit,
    setSubmitFailed,
  } = props

  const theme = useTheme()
  const { analytics } = useFirebase()
  const styles = useMemo(() => createStyles(theme, isEditing), [isEditing, theme])

  const {
    input: { value: ivcfValue },
    meta: metaAbsolute,
  } = usePecField({
    name: name.absolutePath(),
    subscription: { value: true },
  })

  const { meta } = usePecField({ name: name })

  const {
    input: { value: ivcfCacheValue },
  } = usePecField({
    name: soapMeta.objetivo.atendimentosEspecificos.ivcfCache.absolutePath(),
    subscription: { value: true },
  })

  const {
    input: { onChange: updateFormAtivo },
  } = useField<FormAtivoObjetivoEnum>(soapMeta.objetivo.atendimentosEspecificos.formAtivo.absolutePath())

  const submitAtendimentoFalhouPorIVCFNaoPreenchido = getFieldError(metaAbsolute)
  const errorField = getFieldError(meta)
  const { years: idadeCidadaoEmAnos } = calculateAge(cidadaoDataNascimento, dataInicioAtendimento)
  const humanizedIdadeCidadao = humanizeAge(cidadaoDataNascimento, dataInicioAtendimento)
  const isReadOnly = readOnly && !isEditing

  const handleSubmit = (values: IvcfAplicacaoModel, formApi: FormApi<IvcfAplicacaoModel>) => {
    analytics.logEvent('click_calcular_ivcf')
    const scrollTo = rootRef.current?.offsetTop - (isEditing ? 500 : 220)
    setTimeout(() => {
      window.scrollTo({ behavior: 'smooth', top: scrollTo })
    })
    onSubmit(calculateIvcf(values, idadeCidadaoEmAnos))
    updateCache?.(null)
    setTimeout(formApi.reset)
  }

  const handleDesabilitarIvcf = (reset: () => void) => {
    const scrollTo = rootRef.current?.offsetTop - 220
    if (!isEmpty(ivcfCacheValue) || !isEmpty(ivcfValue)) {
      confirm({
        title: 'Deseja descartar os dados do IVCF-20?',
        body: 'As alterações realizadas serão perdidas.',
        cancelLabel: 'Não, manter dados',
        confirmLabel: 'Sim, descartar',
        onConfirm: () => {
          analytics.logEvent('click_cancelar_ivcf')
          updateFormAtivo(null)
          reset()
          updateCache?.(null)
          window.scrollTo({ behavior: 'smooth', top: scrollTo })
        },
      })()
    } else {
      analytics.logEvent('click_cancelar_ivcf')
      updateFormAtivo(null)
      setTimeout(() => {
        window.scrollTo({ behavior: 'smooth', top: scrollTo })
      })
    }
  }

  const handleCancelEdit = (formApi: FormApi<IvcfAplicacaoModel>) => {
    onCancelEdit?.()
    setTimeout(formApi.reset)
  }

  return (
    <Form<IvcfAplicacaoModel>
      onSubmit={handleSubmit}
      validate={ivcfValidator}
      decorators={[ivcfDecorator]}
      initialValues={initialValues}
      render={(renderProps) => {
        const reset = () => {
          renderProps.form.reset()
          renderProps.form.getRegisteredFields().forEach((field) => renderProps.form.resetFieldState(field))
        }

        const submitFailed =
          submitAtendimentoFalhouPorIVCFNaoPreenchido || (renderProps.submitFailed && renderProps.hasValidationErrors)

        setTimeout(() => setSubmitFailed?.(submitFailed))

        const error = submitFailed || !!errorField

        return (
          <Fragment>
            {updateCache && <FormDebouncedValueSpy onChange={({ values }) => updateCache(values)} />}
            <VFlow>
              {error && !isEditing && (
                <Alert type='danger' inline>
                  É necessário responder todas as perguntas do formulário para realizar o cálculo.
                </Alert>
              )}
              <div css={styles.box(error, hideBorder)}>
                {!hideHeader && (
                  <HFlow hSpacing={0.25} alignItems='center' style={styles.header}>
                    <Heading level={4}>Perguntas do instrumento</Heading>
                    <Heading level={4} color='danger'>
                      *
                    </Heading>
                  </HFlow>
                )}

                <VFlow vSpacing={0}>
                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.IDADE} isEditing={isEditing} />
                  <HFlow alignItems='center' style={styles.question}>
                    <Text fontWeight='bold'>{humanizedIdadeCidadao}.</Text>
                  </HFlow>

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.PERCEPCAO_SAUDE} isEditing={isEditing} />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.PERCEPCAO_SAUDE_PERGUNTA_1}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.AVD} isEditing={isEditing} />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.AVD_INSTRUMENTAL}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.AVD_BASICA}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.COGNICAO} isEditing={isEditing} />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COGNICAO_PERGUNTA_1}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COGNICAO_PERGUNTA_2}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COGNICAO_PERGUNTA_3}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.HUMOR} isEditing={isEditing} />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.HUMOR_PERGUNTA_1}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.HUMOR_PERGUNTA_2}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.MOBILIDADE} isEditing={isEditing} />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.ALCANCE_PREENSAO_PINCA}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.CAPACIDADE_AEROBICA_MUSCULAR}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.MARCHA}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.CONTINENCIA_ESFINCTERIANA}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.COMUNICACAO} isEditing={isEditing} />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.VISAO}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfSubgrupoField
                    ivcfPath={formPath}
                    subgrupo={IvcfSubgrupoPerguntaEnum.AUDICAO}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />

                  <IvcfGrupoHeader grupo={IvcfGrupoEnum.COMORBIDADE_MULTIPLA} isEditing={isEditing} />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COMORBIDADE_PERGUNTA_1}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COMORBIDADE_PERGUNTA_2}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                  />
                  <IvcfPerguntaField
                    ivcfPath={formPath}
                    pergunta={IvcfPerguntaEnum.COMORBIDADE_PERGUNTA_3}
                    isEditing={isEditing}
                    readOnly={isReadOnly}
                    hideBorderBottom
                  />

                  {isEditing && (
                    <HFlow justifyContent='flex-end' hSpacing={0.5} style={styles.editingFooter}>
                      <Button size='small' onClick={() => handleCancelEdit(renderProps.form)}>
                        Cancelar
                      </Button>
                      <SubmitButton handleSubmit={renderProps.handleSubmit} size='small'>
                        Salvar
                      </SubmitButton>
                    </HFlow>
                  )}
                </VFlow>
              </div>

              {!readOnly && !isEditing && (
                <HFlow justifyContent='flex-end' hSpacing={0.5}>
                  <Button size='small' onClick={() => handleDesabilitarIvcf(reset)}>
                    Cancelar
                  </Button>
                  <SubmitButton handleSubmit={renderProps.handleSubmit} size='small' data-cy='FormFooter.cancelar'>
                    Calcular índice
                  </SubmitButton>
                </HFlow>
              )}
            </VFlow>
          </Fragment>
        )
      }}
    />
  )
}

const createStyles = (theme: Theme, isEditing: boolean) => ({
  box: (hasErrors: boolean, hideBorder: boolean) => css`
    border: 1px solid ${hasErrors ? theme.pallete.status.danger.main : theme.pallete.gray.c80};
    border-width: ${hideBorder && !hasErrors ? 0 : 1};
    border-radius: 2px;
    overflow: hidden;
  `,
  header: css`
    display: flex;
    align-items: center;
    height: 2.5rem;
    padding: 0 1rem;
    background: ${theme.pallete.surface.main};
    border-bottom: 1px solid ${theme.pallete.gray.c80};
  `,
  question: css`
    padding: 0.5rem 1rem;
    border-bottom: 1px solid ${theme.pallete.gray.c80};
    background: ${isEditing ? theme.pallete.primary.c90 : theme.pallete.surface.main};
  `,
  editingFooter: css`
    padding: 1rem;
    background: ${theme.pallete.primary.c90};
    border-top: 1px solid ${theme.pallete.gray.c80};
  `,
})
